import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import {
  SequenceMenu,
  SequenceMenuDivider,
  SetIsOpen
} from '@sequencehq/core-components'
import { InvoiceMenuItems } from 'Invoices/index'

type InvoiceStatus = DashboardApi20240730.GetInvoice.InvoiceStatus
type PaymentStatus = DashboardApi20240730.GetInvoice.InvoicePaymentStatus

export const InvoiceKebabMenu = (props: {
  invoiceId: string
  invoiceStatus: InvoiceStatus
  paymentStatus: PaymentStatus
  invoiceNumber: string | undefined
  linkedServices: unknown[]
  'data-testid'?: string
}) => {
  const menuItems = []

  menuItems.push(
    <InvoiceMenuItems.PreviewInvoice
      key="preview"
      invoiceId={props.invoiceId}
      invoiceStatus={props.invoiceStatus}
    />
  )

  menuItems.push(
    <InvoiceMenuItems.ViewInvoice key="view" invoiceId={props.invoiceId} />
  )

  menuItems.push(
    <InvoiceMenuItems.DownloadInvoice
      key="download"
      invoiceId={props.invoiceId}
      invoiceNumber={props.invoiceNumber}
      invoiceStatus={props.invoiceStatus}
    />
  )

  menuItems.push(<SequenceMenuDivider key="view-download-divider" />)

  if (props.invoiceStatus === 'DRAFT') {
    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.FinalizeInvoice
        key="finalize"
        invoiceId={props.invoiceId}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))

    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.FinalizeAndSendInvoice
        key="finalizeAndSend"
        invoiceId={props.invoiceId}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))
  }

  if (props.invoiceStatus === 'FINAL') {
    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.SendInvoice
        key="send"
        invoiceId={props.invoiceId}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))
  }

  menuItems.push(
    <InvoiceMenuItems.DuplicateInvoice
      key="duplicate"
      invoiceId={props.invoiceId}
    />
  )

  if (props.invoiceStatus === 'IN_PROGRESS') {
    menuItems.push(<SequenceMenuDivider key="in-progress-divider" />)
  }

  if (props.invoiceStatus === 'DRAFT' || props.invoiceStatus === 'FINAL') {
    menuItems.push(<SequenceMenuDivider key="mark-as-sent-divider" />)
    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.MarkAsSent
        key="markAsSent"
        invoiceId={props.invoiceId}
        invoiceNumber={props.invoiceNumber}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))
  }

  if (props.invoiceStatus === 'SENT' && props.linkedServices.length === 0) {
    menuItems.push(<SequenceMenuDivider key="payment-status-divider" />)

    if (props.paymentStatus !== 'PAID') {
      menuItems.push(
        <InvoiceMenuItems.MarkAsPaid
          key="markAsPaid"
          invoiceId={props.invoiceId}
          invoiceNumber={props.invoiceNumber}
        />
      )
    }

    if (props.paymentStatus !== 'UNCOLLECTIBLE') {
      menuItems.push(
        <InvoiceMenuItems.MarkAsUncollectible
          key="markAsUncollectible"
          invoiceId={props.invoiceId}
          invoiceNumber={props.invoiceNumber}
        />
      )
    }

    if (props.paymentStatus !== 'UNPAID') {
      menuItems.push(
        <InvoiceMenuItems.MarkAsUnpaid
          key="markAsUnpaid"
          invoiceId={props.invoiceId}
          invoiceNumber={props.invoiceNumber}
        />
      )
    }
  }

  if (props.invoiceStatus === 'DRAFT' || props.invoiceStatus === 'FINAL') {
    menuItems.push(<SequenceMenuDivider key="divider" />)

    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.VoidInvoice
        key="void"
        invoiceId={props.invoiceId}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))
  }

  if (
    props.invoiceStatus === 'IN_PROGRESS' ||
    props.invoiceStatus === 'DRAFT'
  ) {
    menuItems.push(({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
      <InvoiceMenuItems.DeleteInvoice
        key="delete"
        invoiceId={props.invoiceId}
        onSuccess={() => setIsOpen?.(false)}
      />
    ))
  }

  return (
    <SequenceMenu
      data-testid={
        props['data-testid']
          ? props['data-testid']
          : `invoices.${props.invoiceId}.menu`
      }
      buttonSize="small"
      alignment="bottom-right"
      items={menuItems}
      closeMenuOnItemClick
    />
  )
}
